import { Capacitor, registerPlugin } from '@capacitor/core';
import { notificationAPI } from './api';

// Access the native plugin via Capacitor bridge directly
// This avoids dynamic import issues in the Capacitor WebView
const PushNotifications = registerPlugin('PushNotifications');

// Register notification tap listener IMMEDIATELY at module load (not inside initialize)
// so it catches the event even when the app is cold-started from a killed state.
// initialize() runs after a 1s delay, by which time the event has already fired and been lost.
if (Capacitor.isNativePlatform()) {
  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    PushNotifications.removeAllDeliveredNotifications().catch(() => {});
    const data = action.notification?.data;
    const url = data?.actionUrl || '/notifications';
    sessionStorage.setItem('pendingNotificationNav', url);
  });
}

class PushNotificationService {
  constructor() {
    this._initialized = false;
    this._token = null;
  }

  /**
   * Get a unique device identifier
   */
  getDeviceId() {
    let deviceId = localStorage.getItem('push_device_id');
    if (!deviceId) {
      deviceId = `${Capacitor.getPlatform()}_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
      localStorage.setItem('push_device_id', deviceId);
    }
    return deviceId;
  }

  /**
   * Initialize push notifications - request permissions and register
   */
  async initialize() {
    // Only works on native platforms
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    if (this._initialized || this._initializing) {
      return;
    }
    this._initializing = true;

    try {
      // Request permission
      const permResult = await PushNotifications.requestPermissions();
      if (permResult.receive !== 'granted') {
        this._initializing = false;
        return;
      }

      // Set up all listeners BEFORE calling register() to avoid race condition

      // Listen for registration success - skip if same token already saved
      PushNotifications.addListener('registration', async (token) => {
        if (this._token === token.value) return;
        this._token = token.value;

        const payload = {
          token: token.value,
          deviceId: this.getDeviceId(),
          platform: Capacitor.getPlatform()
        };

        // Retry up to 3 times with increasing delay
        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            await notificationAPI.registerDeviceToken(payload);
            return;
          } catch (error) {
            console.error(`[Push] Save failed (attempt ${attempt}/3):`, error?.message || error);
            if (attempt < 3) {
              await new Promise(r => setTimeout(r, attempt * 2000));
            }
          }
        }
      });

      // Listen for registration errors
      PushNotifications.addListener('registrationError', (error) => {
        console.error('[Push] Registration error:', error);
      });

      // Listen for push notifications received in foreground
      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        window.dispatchEvent(new CustomEvent('pushNotificationReceived', {
          detail: {
            title: notification.title,
            body: notification.body,
            data: notification.data
          }
        }));
      });

      // Note: pushNotificationActionPerformed listener is registered at module load
      // (above the class) so it catches taps even on cold start from killed state.

      // Register AFTER listeners are set up
      await PushNotifications.register();

      this._initialized = true;
    } catch (error) {
      this._initializing = false;
      console.error('[Push] Initialization error:', error);
    }
  }

  /**
   * Unregister push notifications (on logout)
   */
  async unregister() {
    if (!Capacitor.isNativePlatform() || !this._initialized) {
      return;
    }

    try {
      const deviceId = this.getDeviceId();
      await notificationAPI.unregisterDeviceToken(deviceId);
    } catch (error) {
      console.error('[Push] Failed to unregister token:', error);
    }

    try {
      await PushNotifications.removeAllListeners();
    } catch (error) {
      console.error('[Push] Failed to remove listeners:', error);
    }

    this._initialized = false;
    this._initializing = false;
    this._token = null;
  }
}

const pushNotificationService = new PushNotificationService();
export default pushNotificationService;
